import React from 'react';
import {
  ApolloQueryResult,
  OperationVariables,
  useLazyQuery,
} from '@apollo/client';
import { DashboardFilters } from '../components/dashboard/GoalsDashboard/reducer';
import {
  GET_BENEFIT,
  GET_COMPANY_EMPLOYEE_METRIC,
  GET_COMPANY_METRICS_RESULT,
  GET_COMPANY_POINTS,
  GET_TOTAL_REWARDED_BY_COMPANY,
  GET_USERS_TOTAL_POINTS_BY_COMPANY,
} from '../graphql/queries';
import { Benefit, Company, User } from '../models';
import { buildFilterArray } from '../lib/goalsDashboard';

interface ChartData {
  labels: string[];
  data: number[];
  loading: boolean;
  kpiInfo: {
    target: number;
    totalTarget: number;
    totalCollected: number;
  };
}

type TUseGoalsDashboard = {
  leaderBoard: {
    data: {
      totalRewarded: number;
      user: User;
    }[];
    loading: boolean;
  };
  totalPoints: {
    pointsAssigned: number;
    pointsBalance: number;
    pointsRedeemed: number;
    refetchCompayPoints: (variables?: Partial<OperationVariables>) => Promise<
      ApolloQueryResult<{
        getCompanyTotalPoints: {
          totalPoints: number;
          benefits: {
            price: number;
          };
        };
      }>
    >;
  };
  doughnut: ChartData;
  loading: boolean;
  refreshDashboard: () => void;
};

export function useGoalsDashboard(
  company: Company,
  state: Partial<DashboardFilters> = {}
): TUseGoalsDashboard {
  const [
    getCompanyMetrics,
    { data: dataCompanyMetrics, loading: loadingCompanyMetrics },
  ] = useLazyQuery<{
    getCompanyMetricsResult: {
      expectedResult: number;
      result: number[];
      labels: string[];
      target: number;
      totalTarget: number;
      totalCollected: number;
    };
  }>(GET_COMPANY_METRICS_RESULT, {});

  const [
    getCompanyEmployeeMetrics,
    {
      data: dataCompanyEmployeeMetrics,
      loading: loadingCompanyEmployeeMetrics,
    },
  ] = useLazyQuery<{
    getCompanyEmployeeMetricsResult: {
      totalRewarded: number;
      user: User;
    }[];
  }>(GET_COMPANY_EMPLOYEE_METRIC, {});

  // PUNTOS DE DISPONIBLES POR EMPRESA
  const [getCompanyPoints, { data: dataCompanyPoints, refetch }] =
    useLazyQuery<{
      getCompanyTotalPoints: {
        totalPoints: number;
        benefits: {
          price: number;
        };
      };
    }>(GET_COMPANY_POINTS, {});

  // PUNTOS RECOMPENSADOS A EMPLEAODS POR EMPRESA
  const [getRewardedPointsByCompany, { data: dataRewardedPointsByCompany }] =
    useLazyQuery<{
      getTotalRewardedByCompany: {
        totalRewarded: number;
      };
    }>(GET_TOTAL_REWARDED_BY_COMPANY, {});

  const [
    getUsersTotalPointsByCompany,
    { data: dataUsersTotalPointsByCompany },
  ] = useLazyQuery<{
    getUsersTotalPointsByCompany: {
      totalPoints: number;
    };
  }>(GET_USERS_TOTAL_POINTS_BY_COMPANY, {});

  const [getBenefit] = useLazyQuery<{
    benefit: Benefit;
  }>(GET_BENEFIT, {});

  // Se necesita un query para obtener los puntos disponibles por empresa
  const refreshDashboard = () => {
    getCompanyMetrics({
      variables: {
        data: {
          companyId: (company as Company)._id,
          goalId: state.goal,
          ...(state.startDate && { startDate: state.startDate }),
          ...(state.endDate && { endDate: state.endDate }),
          filter: buildFilterArray(state),
          periodId: state.period,
        },
      },
    });
    getCompanyEmployeeMetrics({
      variables: {
        data: {
          companyId: (company as Company)._id,
          goalId: state.goal,
          ...(state.startDate && { startDate: state.startDate }),
          ...(state.endDate && { endDate: state.endDate }),
          filter: buildFilterArray(state),
          periodId: state.period,
        },
      },
    });
    getCompanyPoints({
      variables: {
        data: {
          companyId: (company as Company)._id,
        },
      },
    });
    getRewardedPointsByCompany({
      variables: {
        data: {
          companyId: (company as Company)._id,
        },
      },
    });
    getUsersTotalPointsByCompany({
      variables: {
        data: {
          companyId: (company as Company)._id,
        },
      },
    });
    getBenefit({
      variables: {
        filter: { company: (company as Company)._id },
      },
    });
  };

  React.useEffect(() => {
    refreshDashboard();
  }, [
    state.startDate,
    state.endDate,
    state.fieldType,
    state.goal,
    state.period,
  ]);

  const pointsRedeemed = React.useMemo(() => {
    const totalRewarded =
      dataUsersTotalPointsByCompany?.getUsersTotalPointsByCompany?.totalPoints;
    const totalPoints =
      dataRewardedPointsByCompany?.getTotalRewardedByCompany?.totalRewarded;
    return totalPoints - totalRewarded;
  }, [dataUsersTotalPointsByCompany?.getUsersTotalPointsByCompany]);

  return {
    refreshDashboard,
    leaderBoard: {
      data: dataCompanyEmployeeMetrics?.getCompanyEmployeeMetricsResult,
      loading: loadingCompanyEmployeeMetrics,
    },
    totalPoints: {
      // PUNTOS DE DISPONIBLES POR EMPRESA
      pointsBalance: dataCompanyPoints?.getCompanyTotalPoints?.totalPoints,
      // PUNTOS RECOMPENSADOS A EMPLEAODS POR EMPRESA
      pointsAssigned:
        dataRewardedPointsByCompany?.getTotalRewardedByCompany?.totalRewarded,
      pointsRedeemed,
      refetchCompayPoints: refetch,
    },
    doughnut: {
      labels: dataCompanyMetrics?.getCompanyMetricsResult?.labels ?? [],
      data: dataCompanyMetrics?.getCompanyMetricsResult?.result ?? [],
      loading: loadingCompanyMetrics,
      kpiInfo: {
        target: dataCompanyMetrics?.getCompanyMetricsResult?.target,
        totalTarget: dataCompanyMetrics?.getCompanyMetricsResult?.totalTarget,
        totalCollected:
          dataCompanyMetrics?.getCompanyMetricsResult?.totalCollected,
      },
    },
    loading: loadingCompanyMetrics || loadingCompanyEmployeeMetrics,
  };
}
